import React from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { MessagePartInputExtended } from "@/pages/BulkMessaging/types";
import { PhonePreview } from "../PhoneFrame/PhonePreview";
import { MessageBubble } from "./MessageBubble";
import { AddElementSeparator } from "./AddElementSeparator";
import { useMessageElements, useDragAndDrop, useAddMenu } from "./hooks";

interface MessageEditorProps {
  messageParts: MessagePartInputExtended[];
  onChange: (messageParts: MessagePartInputExtended[]) => void;
  className?: string;
  showStatusBar?: boolean;
  showHomeIndicator?: boolean;
  contentClassName?: string;
  frameClassName?: string;
  senderName?: string;
  senderProfilePhoto?: { url?: string | null } | null;
}

export const MessageEditor = ({
  messageParts,
  onChange,
  className,
  showStatusBar = true,
  showHomeIndicator = true,
  contentClassName,
  frameClassName,
  senderName,
  senderProfilePhoto,
}: MessageEditorProps) => {
  const {
    handleEditMessagePart,
    handleDeleteMessagePart,
    handleAddText,
    handleAddImage,
  } = useMessageElements({ messageParts, onChange });

  const { handleDragEnd } = useDragAndDrop({ messageParts, onChange });

  const { showAddMenu, handleShowAddMenu } = useAddMenu();

  const onAddText = () => {
    handleAddText();
    handleShowAddMenu(false);
  };

  const onAddImage = () => {
    handleAddImage();
    handleShowAddMenu(false);
  };

  const showControls = messageParts.length > 1;

  return (
    <PhonePreview
      className={className}
      showStatusBar={showStatusBar}
      showHomeIndicator={showHomeIndicator}
      contentClassName={contentClassName}
      frameClassName={frameClassName}
    >
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={messageParts.map((part, index) => `${index}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-6 overflow-hidden">
            {messageParts.map((messagePart, index) => (
              <MessageBubble
                key={`${index}`}
                messagePart={messagePart}
                index={index}
                onEdit={handleEditMessagePart}
                onDelete={handleDeleteMessagePart}
                senderName={senderName}
                senderProfilePhoto={senderProfilePhoto}
                showControls={showControls}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <AddElementSeparator
        showAddMenu={showAddMenu}
        onShowAddMenu={handleShowAddMenu}
        onAddText={onAddText}
        onAddImage={onAddImage}
      />
    </PhonePreview>
  );
};
